import React, { useEffect, useState, useRef } from 'react'; // Import useRef
import { useDispatch, useSelector } from 'react-redux';
import {
    setApiLoaded,
    setSignedIn,
    setSelectedFile,
    setError,
} from '../features/googleDrive/googleDriveSlice';

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/drive.file';

const GoogleDrivePicker = () => {
    const dispatch = useDispatch();
    const [fileContent, setFileContent] = useState('');
    const { isApiLoaded, isSignedIn, selectedFile } = useSelector(
        (state) => state.googleDrive
    );

    // Use useRef to hold the token client instance across renders
    const tokenClient = useRef(null);

    useEffect(() => {

        const fetchFileContent = async (file) => {
            // Now we get the mimeType directly from the 'file' object passed as an argument
            console.log(file);
            const fileId = file.id;
            const mimeType = file.mimeType;
    
            try {
                let response;
                // For Google Docs, we need to export the content.
                if (mimeType === 'application/vnd.google-apps.document') {
                    response = await window.gapi.client.drive.files.export({
                        fileId: fileId,
                        mimeType: 'text/plain',
                    });
                    console.log(response)
                } else {
                    // For other file types like plain text, use 'files.get' with alt=media.
                    response = await window.gapi.client.drive.files.get({
                        fileId: fileId,
                        alt: 'media',
                    });
                    console.log(response)
                }
                setFileContent(response.body);
            } catch (error) {
                console.error('Error fetching file content:', error);
                dispatch(setError('Could not fetch file content. See console for details.'));
            }
        };

        const initializeGapiClient = async () => {
            try {
                await window.gapi.client.init({
                    apiKey: API_KEY,
                    // The client_id is not needed here for the new GIS flow
                    discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'],
                });
                dispatch(setApiLoaded());
            } catch (error) {
                dispatch(setError(error.message));
            }
        };

        const createPicker = (accessToken) => {
            const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
            const picker = new window.google.picker.PickerBuilder()
                // The App ID is the first part of your numeric Client ID
                .setAppId(CLIENT_ID.split('-')[0])
                .setOAuthToken(accessToken)
                .addView(view)
                .setDeveloperKey(API_KEY)
                .setCallback(pickerCallback)
                .build();
            picker.setVisible(true);
        };

        const pickerCallback = (data) => {
            if (data.action === window.google.picker.Action.PICKED) {
                const doc = data.docs[0];
                console.log("your document:")
                console.log(doc)
                dispatch(setSelectedFile({
                    id: doc.id,
                    name: doc.name,
                    mimeType: doc.mimeType,
                    url: doc.url,
                }));

                fetchFileContent(doc);
            }
        };

        const initializeGisClient = () => {
            // Check if CLIENT_ID is loaded before initializing
            if (!CLIENT_ID || !SCOPES) {
                console.error("CLIENT_ID or SCOPES is missing. Check your .env file and ensure it's loaded correctly.");
                dispatch(setError("Google Drive client ID is not configured."));
                return;
            }

            // Store the initialized client in the ref's .current property
            tokenClient.current = window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: SCOPES,
                callback: (tokenResponse) => {
                    if (tokenResponse && tokenResponse.access_token) {
                        dispatch(setSignedIn(true));
                        createPicker(tokenResponse.access_token);
                    } else {
                        dispatch(setError('Failed to get access token.'));
                    }
                },
            });
        };

        const gapiScript = document.createElement('script');
        gapiScript.src = 'https://apis.google.com/js/api.js';
        gapiScript.onload = () => window.gapi.load('client:picker', initializeGapiClient);
        document.body.appendChild(gapiScript);

        const gisScript = document.createElement('script');
        gisScript.src = 'https://accounts.google.com/gsi/client';
        gisScript.onload = initializeGisClient;
        document.body.appendChild(gisScript);

        // Cleanup scripts on component unmount
        return () => {
            document.body.removeChild(gapiScript);
            document.body.removeChild(gisScript);
        };

    }, [dispatch]); // Empty dependency array ensures this runs only once on mount


    const handleAuthClick = () => {
        // Access the client from the ref's .current property
        if (tokenClient.current) {
            // The gapi.client.getToken() check is for the older gapi.auth2 flow
            // and is not reliable with the new GIS flow. We just request a token.
            tokenClient.current.requestAccessToken({ prompt: 'consent' });
        } else {
            dispatch(setError('Google authentication client is not ready.'));
        }
    };



    return (
        <div>
            <button onClick={handleAuthClick} disabled={!isApiLoaded || !tokenClient.current}>
                {isSignedIn ? 'Select Another File' : 'Connect to Google Drive'}
            </button>
            {selectedFile && (
                <div>
                    <h3>Selected File:</h3>
                    <p>
                        <strong>Name:</strong> {selectedFile.name}
                    </p>
                    <p>
                        <strong>MIME Type:</strong> {selectedFile.mimeType}
                    </p>
                    <p>
                        <strong>URL:</strong> <a href={selectedFile.url} target="_blank" rel="noopener noreferrer">Link</a>
                    </p>
                    {fileContent && (
                        <div>
                            <h3>File Content:</h3>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                border: '1px solid #ccc',
                                padding: '10px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                {fileContent}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GoogleDrivePicker;